import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Plus, Trash2, Upload, Download, ClipboardPaste, Copy, RotateCcw } from "lucide-react";
import { AISuggestionButton } from "./AISuggestionButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface MCQRow {
  question: string;
  category: string;
  subCategory: string;
  tags: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  option5: string;
  correctAnswer: string;
  explanation: string;
}

const INITIAL_ROW: MCQRow = {
  question: "",
  category: "",
  subCategory: "",
  tags: "",
  option1: "",
  option2: "",
  option3: "",
  option4: "",
  option5: "",
  correctAnswer: "",
  explanation: "",
};

// Available data for auto-completion
const CATEGORIES = ["Cardiology", "Diabetes & Endocrinology", "Neurology"];
const TAGS = ["Cardiology", "Diabetes", "Endocrinology", "Stroke", "ECG", "Neurology"];

const BulkPasteTable = () => {
  // Initialize with sample data to show table structure
  const sampleRows: MCQRow[] = [
    {
      question: "What is the most common cause of chest pain in emergency departments?",
      category: "Cardiology",
      subCategory: "Emergency Medicine",
      tags: "chest pain, emergency, diagnosis",
      option1: "Myocardial infarction",
      option2: "Gastroesophageal reflux",
      option3: "Musculoskeletal pain",
      option4: "Pulmonary embolism",
      option5: "Anxiety",
      correctAnswer: "Musculoskeletal pain",
      explanation: "Musculoskeletal chest pain is the most common cause of chest pain in emergency departments, accounting for approximately 50% of cases."
    },
    {
      question: "Which medication is first-line treatment for type 2 diabetes?",
      category: "Diabetes & Endocrinology", 
      subCategory: "Pharmacology",
      tags: "diabetes, medication, treatment",
      option1: "Insulin",
      option2: "Metformin",
      option3: "Sulfonylureas",
      option4: "DPP-4 inhibitors",
      option5: "",
      correctAnswer: "Metformin",
      explanation: "Metformin is recommended as first-line therapy for type 2 diabetes due to its efficacy, safety profile, and cardiovascular benefits."
    },
    { ...INITIAL_ROW }
  ];
  
  const [rows, setRows] = useState<MCQRow[]>(sampleRows);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pasteText, setPasteText] = useState("");
  const [focusedCell, setFocusedCell] = useState<{ row: number; col: string } | null>(null);
  const [history, setHistory] = useState<MCQRow[][]>([]);
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement }>({});

  const parseCSVData = (text: string) => {
    if (!text.trim()) return;

    const lines = text.trim().split('\n').filter(line => line.trim());
    if (lines.length === 0) return;

    // Enhanced delimiter detection with column counting
    const delimiters = ['\t', ',', '|'];
    let bestDelimiter = '\t';
    let maxColumns = 0;

    // Test each delimiter and choose the one that gives us the most consistent column count
    for (const del of delimiters) {
      const testLine = lines[0];
      if (testLine.includes(del)) {
        const columnCount = testLine.split(del).length;
        if (columnCount > maxColumns && columnCount >= 6) { // At least 6 columns needed
          maxColumns = columnCount;
          bestDelimiter = del;
        }
      }
    }

    const newRows: MCQRow[] = [];

    for (const line of lines.slice(0, 50)) { // Limit to 50 rows
      if (!line.trim()) continue;

      // Enhanced parsing that preserves empty fields in correct positions
      let columns = line.split(bestDelimiter);
      
      // Clean up columns but preserve empty strings
      columns = columns.map(col => col.trim().replace(/^["']|["']$/g, ''));

      // Ensure we have at least 11 columns by padding with empty strings
      while (columns.length < 11) {
        columns.push('');
      }

      if (columns.length >= 6) { // Minimum required fields
        const row: MCQRow = {
          question: columns[0] || "",
          category: columns[1] || "",
          subCategory: columns[2] || "", // Now properly preserves empty sub-categories
          tags: columns[3] || "", // Now properly preserves empty tags
          option1: columns[4] || "",
          option2: columns[5] || "",
          option3: columns[6] || "",
          option4: columns[7] || "",
          option5: columns[8] || "",
          correctAnswer: columns[9] || "",
          explanation: columns[10] || "",
        };
        newRows.push(row);
      }
    }

    if (newRows.length > 0) {
      if (newRows.length > 50) {
        toast({
          title: "Too Many Rows",
          description: `Only the first 50 rows will be added. ${newRows.length - 50} rows were truncated.`,
          variant: "destructive",
        });
        newRows.splice(50);
      }
      setRows(newRows);
      setPasteText("");
      toast({
        title: "Data Parsed",
        description: `Successfully parsed ${newRows.length} MCQ(s) from the pasted data.`,
      });
    } else {
      toast({
        title: "Parse Error",
        description: "Could not parse the pasted data. Please check the format.",
        variant: "destructive",
      });
    }
  };

  // Save state to history for undo
  const saveToHistory = useCallback(() => {
    setHistory(prev => [...prev.slice(-9), JSON.parse(JSON.stringify(rows))]);
  }, [rows]);

  const updateRow = (index: number, field: keyof MCQRow, value: string) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);
  };

  const duplicateRow = (index: number) => {
    if (rows.length >= 50) {
      toast({
        title: "Limit Reached",
        description: "Maximum 50 rows allowed.",
        variant: "destructive",
      });
      return;
    }
    const newRows = [...rows];
    newRows.splice(index + 1, 0, { ...rows[index] });
    setRows(newRows);
  };

  const undo = () => {
    if (history.length > 0) {
      setRows(history[history.length - 1]);
      setHistory(prev => prev.slice(0, -1));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, field: keyof MCQRow) => {
    const fields: (keyof MCQRow)[] = ['question', 'category', 'subCategory', 'tags', 'option1', 'option2', 'option3', 'option4', 'option5', 'correctAnswer', 'explanation'];
    const currentFieldIndex = fields.indexOf(field);
    
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const nextField = fields[currentFieldIndex + 1];
      if (nextField) {
        const nextRef = inputRefs.current[`${rowIndex}-${nextField}`];
        nextRef?.focus();
      } else if (rowIndex < rows.length - 1) {
        const nextRef = inputRefs.current[`${rowIndex + 1}-${fields[0]}`];
        nextRef?.focus();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const prevField = fields[currentFieldIndex - 1];
      if (prevField) {
        const prevRef = inputRefs.current[`${rowIndex}-${prevField}`];
        prevRef?.focus();
      } else if (rowIndex > 0) {
        const prevRef = inputRefs.current[`${rowIndex - 1}-${fields[fields.length - 1]}`];
        prevRef?.focus();
      }
    } else if (e.key === 'Enter' && rowIndex === rows.length - 1) {
      addRow();
      setTimeout(() => {
        const nextRef = inputRefs.current[`${rowIndex + 1}-${fields[0]}`];
        nextRef?.focus();
      }, 100);
    } else if (e.key === 'Enter') {
      const nextRef = inputRefs.current[`${rowIndex + 1}-${field}`];
      nextRef?.focus();
    }
  };

  const addRow = () => {
    if (rows.length >= 50) {
      toast({
        title: "Limit Reached",
        description: "Maximum 50 rows allowed.",
        variant: "destructive",
      });
      return;
    }
    setRows([...rows, { ...INITIAL_ROW }]);
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
  };

  const validateRow = (row: MCQRow): string[] => {
    const errors: string[] = [];
    if (!row.question.trim()) errors.push("Question is required");
    if (!row.option1.trim()) errors.push("Option 1 is required");
    if (!row.option2.trim()) errors.push("Option 2 is required");
    if (!row.correctAnswer.trim()) errors.push("Correct answer is required");
    
    const options = [row.option1, row.option2, row.option3, row.option4, row.option5].filter(opt => opt.trim());
    const correctAnswer = row.correctAnswer.trim();
    
    // Check if correct answer is numeric (1-5)
    const numericAnswer = parseInt(correctAnswer);
    if (!isNaN(numericAnswer) && numericAnswer >= 1 && numericAnswer <= 5) {
      // Validate numeric answer against available options
      if (numericAnswer > options.length) {
        errors.push(`Option ${numericAnswer} doesn't exist (only ${options.length} options provided)`);
      } else if (!options[numericAnswer - 1]?.trim()) {
        errors.push(`Option ${numericAnswer} is empty`);
      }
    } else {
      // Validate text-based answer (legacy support)
      if (!options.includes(correctAnswer)) {
        errors.push("Correct answer must match one of the options or be a number (1-5)");
      }
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    // Validate all rows
    const validRows: any[] = [];
    const errors: string[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowErrors = validateRow(row);
      
      if (rowErrors.length > 0) {
        errors.push(`Row ${i + 1}: ${rowErrors.join(", ")}`);
        continue;
      }

      // Build options array
      const options = [row.option1, row.option2, row.option3, row.option4, row.option5]
        .filter(opt => opt.trim());
      
      // Convert numeric correct answer to option text
      let correctAnswer = row.correctAnswer.trim();
      const numericAnswer = parseInt(correctAnswer);
      if (!isNaN(numericAnswer) && numericAnswer >= 1 && numericAnswer <= 5) {
        correctAnswer = options[numericAnswer - 1]; // Convert 1-based to 0-based index
      }
      
      const correctIndex = options.indexOf(correctAnswer);

      validRows.push({
        question: row.question.trim(),
        options,
        correctAnswer: correctAnswer,
        explanation: row.explanation.trim() || null,
        category: row.category.trim() || null,
        subCategory: row.subCategory.trim() || null,
        tags: row.tags.trim() ? row.tags.split(',').map(t => t.trim()).filter(t => t) : [],
      });
    }

    if (errors.length > 0) {
      toast({
        title: "Validation Errors",
        description: `${errors.length} error(s) found. Please fix them before submitting.`,
        variant: "destructive",
      });
      return;
    }

    if (validRows.length === 0) {
      toast({
        title: "No Valid Data",
        description: "Please add some valid MCQ data before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setProgress(0);

    try {
      const { data, error } = await supabase.functions.invoke('import-mcqs', {
        body: { records: validRows, baseIndex: 0 }
      });

      if (error) throw error;

      setProgress(100);
      toast({
        title: "Import Successful",
        description: `Successfully imported ${data?.upserted || validRows.length} MCQ(s).`,
      });

      // Reset form
      setRows([{ ...INITIAL_ROW }]);
      
    } catch (error: any) {
      console.error("Import error:", error);
      toast({
        title: "Import Failed",
        description: error.message || "An error occurred during import.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  const exportToCSV = () => {
    const headers = ["Question", "Category", "Sub-Category", "Tags", "Option1", "Option2", "Option3", "Option4", "Option5", "CorrectAnswer", "Explanation"];
    const csvContent = [
      headers.join(','),
      ...rows.map(row => [
        `"${row.question}"`,
        `"${row.category}"`,
        `"${row.subCategory}"`,
        `"${row.tags}"`,
        `"${row.option1}"`,
        `"${row.option2}"`,
        `"${row.option3}"`,
        `"${row.option4}"`,
        `"${row.option5}"`,
        `"${row.correctAnswer}"`,
        `"${row.explanation}"`,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_mcqs.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Auto-complete component
  const AutoCompleteInput = ({ 
    value, 
    onChange, 
    onKeyDown,
    placeholder, 
    options, 
    className,
    fieldRef
  }: {
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    placeholder: string;
    options: string[];
    className?: string;
    fieldRef: (el: HTMLInputElement) => void;
  }) => {
    const [open, setOpen] = useState(false);
    const [isInteracting, setIsInteracting] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();
    
    const filteredOptions = options.filter(option => 
      option.toLowerCase().includes(value.toLowerCase())
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      if (e.target.value.length > 0 && filteredOptions.length > 0) {
        setOpen(true);
      }
    };

    const handleFocus = () => {
      setIsInteracting(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (value.length > 0 && filteredOptions.length > 0) {
        setOpen(true);
      }
    };

    const handleBlur = () => {
      setIsInteracting(false);
      timeoutRef.current = setTimeout(() => {
        if (!isInteracting) {
          setOpen(false);
        }
      }, 150);
    };

    const handleSelect = (option: string) => {
      onChange(option);
      setOpen(false);
      setIsInteracting(false);
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <Popover open={open} onOpenChange={(newOpen) => {
        if (!newOpen) {
          setOpen(false);
          setIsInteracting(false);
        }
      }}>
        <PopoverTrigger asChild>
          <Input
            ref={fieldRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                setIsInteracting(false);
              }
              onKeyDown(e);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={className}
          />
        </PopoverTrigger>
        <PopoverContent 
          className="w-[200px] p-0 z-50" 
          align="start"
          onMouseEnter={() => setIsInteracting(true)}
          onMouseLeave={() => setIsInteracting(false)}
        >
          <Command>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option}
                  onSelect={() => handleSelect(option)}
                >
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="space-y-6">
      {/* Paste Section */}
      <Card className="medical-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardPaste className="h-5 w-5" />
            Paste CSV Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="paste-data">Paste your CSV data here (tab-separated, comma-separated, or pipe-separated)</Label>
            <Textarea
              id="paste-data"
              placeholder="Question	Category	Sub-Category	Tags	Option1	Option2	Option3	Option4	Option5	CorrectAnswer	Explanation&#10;What is the capital of France?	Geography	European Capitals	countries,cities	Paris	London	Berlin	Madrid		Paris	France is located in Europe"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="min-h-[120px] mt-2"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => parseCSVData(pasteText)}
              disabled={!pasteText.trim()}
              variant="outline"
            >
              Parse Data
            </Button>
            <Button 
              onClick={() => setPasteText("")}
              variant="ghost"
              size="sm"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Card className="medical-card border-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">MCQ Data Table (13 Columns)</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10">
                {rows.length}/50 rows
              </Badge>
              <Button onClick={addRow} disabled={rows.length >= 50} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" />
                Add Row
              </Button>
              <Button onClick={undo} disabled={history.length === 0} size="sm" variant="outline">
                <RotateCcw className="h-4 w-4 mr-1" />
                Undo
              </Button>
              <Button onClick={exportToCSV} size="sm" variant="outline">
                <Download className="h-4 w-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 bg-muted/50 rounded-lg border">
            <p className="text-sm font-medium text-muted-foreground">
              📋 This table has 13 columns. Use horizontal scroll to see all columns. Sample data is pre-loaded to show the structure.
            </p>
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto border-2 rounded-lg bg-background shadow-sm">
            <Table className="border-separate border-spacing-0">
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="border-b-2">
                  <TableHead className="w-[40px] bg-muted/80 border-r font-semibold">#</TableHead>
                  <TableHead className="min-w-[300px] bg-muted/80 border-r font-semibold">Question</TableHead>
                  <TableHead className="min-w-[120px] bg-muted/80 border-r font-semibold">Category</TableHead>
                  <TableHead className="min-w-[120px] bg-muted/80 border-r font-semibold">Sub-Category</TableHead>
                  <TableHead className="min-w-[120px] bg-muted/80 border-r font-semibold">Tags</TableHead>
                  <TableHead className="min-w-[150px] bg-muted/80 border-r font-semibold">Option 1</TableHead>
                  <TableHead className="min-w-[150px] bg-muted/80 border-r font-semibold">Option 2</TableHead>
                  <TableHead className="min-w-[150px] bg-muted/80 border-r font-semibold">Option 3</TableHead>
                  <TableHead className="min-w-[150px] bg-muted/80 border-r font-semibold">Option 4</TableHead>
                  <TableHead className="min-w-[150px] bg-muted/80 border-r font-semibold">Option 5</TableHead>
                  <TableHead className="min-w-[150px] bg-muted/80 border-r font-semibold">Correct Answer</TableHead>
                  <TableHead className="min-w-[200px] bg-muted/80 border-r font-semibold">Explanation</TableHead>
                  <TableHead className="w-[120px] bg-muted/80 font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => {
                  const errors = validateRow(row);
                  const hasErrors = errors.length > 0;
                  
                  return (
                    <TableRow 
                      key={index} 
                      className={`group hover:bg-muted/50 ${hasErrors ? "bg-destructive/5" : ""}`}
                      onMouseEnter={() => saveToHistory()}
                    >
                      <TableCell className="font-medium sticky left-0 bg-background">
                        <div className="flex items-center gap-1">
                          {index + 1}
                          {hasErrors && <AlertTriangle className="h-4 w-4 text-destructive" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Textarea
                          ref={(el) => el && (inputRefs.current[`${index}-question`] = el)}
                          value={row.question}
                          onChange={(e) => updateRow(index, 'question', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'question')}
                          className="min-h-[60px] resize-none border-0 focus:ring-1 focus:ring-primary"
                          placeholder="Enter question..."
                        />
                      </TableCell>
                      <TableCell>
                        <AutoCompleteInput
                          value={row.category}
                          onChange={(value) => updateRow(index, 'category', value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'category')}
                          placeholder="Category"
                          options={CATEGORIES}
                          className="border-0 focus:ring-1 focus:ring-primary"
                          fieldRef={(el) => el && (inputRefs.current[`${index}-category`] = el)}
                        />
                      </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Input
                              ref={(el) => el && (inputRefs.current[`${index}-subCategory`] = el)}
                              value={row.subCategory}
                              onChange={(e) => updateRow(index, 'subCategory', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'subCategory')}
                              placeholder={row.subCategory === "" ? "Sub-category (optional)" : "Sub-category"}
                              className={`border-0 focus:ring-1 focus:ring-primary ${row.subCategory === "" ? "bg-muted/30 italic" : ""}`}
                            />
                            <AISuggestionButton
                              question={row.question}
                              category={row.category}
                              type="subcategories"
                              onSelect={(suggestion) => updateRow(index, 'subCategory', suggestion)}
                              disabled={!row.question?.trim() || row.question.length < 10}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <AutoCompleteInput
                              value={row.tags}
                              onChange={(value) => updateRow(index, 'tags', value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'tags')}
                              placeholder={row.tags === "" ? "tag1, tag2 (optional)" : "tag1, tag2"}
                              options={TAGS}
                              className={`border-0 focus:ring-1 focus:ring-primary ${row.tags === "" ? "bg-muted/30 italic" : ""}`}
                              fieldRef={(el) => el && (inputRefs.current[`${index}-tags`] = el)}
                            />
                            <AISuggestionButton
                              question={row.question}
                              category={row.category}
                              existingTags={row.tags ? row.tags.split(',').map(t => t.trim()) : []}
                              type="tags"
                              onSelect={(suggestion) => {
                                const currentTags = row.tags ? row.tags.split(',').map(t => t.trim()).filter(t => t) : [];
                                if (!currentTags.includes(suggestion)) {
                                  const newTags = [...currentTags, suggestion].join(', ');
                                  updateRow(index, 'tags', newTags);
                                }
                              }}
                              disabled={!row.question?.trim() || row.question.length < 10}
                            />
                          </div>
                        </TableCell>
                      <TableCell>
                        <Input
                          ref={(el) => el && (inputRefs.current[`${index}-option1`] = el)}
                          value={row.option1}
                          onChange={(e) => updateRow(index, 'option1', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'option1')}
                          placeholder="Option 1"
                          className="border-0 focus:ring-1 focus:ring-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          ref={(el) => el && (inputRefs.current[`${index}-option2`] = el)}
                          value={row.option2}
                          onChange={(e) => updateRow(index, 'option2', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'option2')}
                          placeholder="Option 2"
                          className="border-0 focus:ring-1 focus:ring-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          ref={(el) => el && (inputRefs.current[`${index}-option3`] = el)}
                          value={row.option3}
                          onChange={(e) => updateRow(index, 'option3', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'option3')}
                          placeholder="Option 3"
                          className="border-0 focus:ring-1 focus:ring-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          ref={(el) => el && (inputRefs.current[`${index}-option4`] = el)}
                          value={row.option4}
                          onChange={(e) => updateRow(index, 'option4', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'option4')}
                          placeholder="Option 4"
                          className="border-0 focus:ring-1 focus:ring-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          ref={(el) => el && (inputRefs.current[`${index}-option5`] = el)}
                          value={row.option5}
                          onChange={(e) => updateRow(index, 'option5', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'option5')}
                          placeholder="Option 5"
                          className="border-0 focus:ring-1 focus:ring-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          ref={(el) => el && (inputRefs.current[`${index}-correctAnswer`] = el)}
                          value={row.correctAnswer}
                          onChange={(e) => updateRow(index, 'correctAnswer', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'correctAnswer')}
                          placeholder="1, 2, 3, 4, or 5"
                          className="border-0 focus:ring-1 focus:ring-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea
                          ref={(el) => el && (inputRefs.current[`${index}-explanation`] = el)}
                          value={row.explanation}
                          onChange={(e) => updateRow(index, 'explanation', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'explanation')}
                          className="min-h-[60px] resize-none border-0 focus:ring-1 focus:ring-primary"
                          placeholder="Explanation..."
                        />
                      </TableCell>
                      <TableCell className="sticky right-0 bg-background">
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            onClick={() => duplicateRow(index)}
                            disabled={rows.length >= 50}
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Duplicate row"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => removeRow(index)}
                            disabled={rows.length <= 1}
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            title="Delete row"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Submit Section */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {rows.filter(row => validateRow(row).length === 0).length} of {rows.length} rows are valid
              </div>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting || rows.every(row => validateRow(row).length > 0)}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                {isSubmitting ? "Importing..." : `Import ${rows.filter(row => validateRow(row).length === 0).length} MCQ(s)`}
              </Button>
            </div>
            
            {isSubmitting && (
              <div className="mt-4">
                <Progress value={progress} className="w-full" />
                <p className="text-sm text-muted-foreground mt-2">
                  Importing MCQs... {progress}%
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Format Help */}
      <Card className="medical-card">
        <CardHeader>
          <CardTitle>Supported Formats</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <h3 className="font-medium">Correct Column Order:</h3>
          <p className="text-muted-foreground mb-4">
            Question → Category → Sub-Category → Tags → Option1 → Option2 → Option3 → Option4 → Option5 → CorrectAnswer → Explanation
          </p>
          
          <div>
            <strong>Tab-separated (from Excel/Google Sheets):</strong>
            <code className="block mt-1 p-2 bg-muted rounded text-xs">
              Question	Category		Tags	Option1	Option2	Option3	Option4	Option5	CorrectAnswer	Explanation
            </code>
            <p className="text-xs text-muted-foreground mt-1">Note: Empty sub-category shown as double tab (		)</p>
          </div>
          
          <div>
            <strong>Comma-separated:</strong>
            <code className="block mt-1 p-2 bg-muted rounded text-xs">
              "Question","Category","","Tags","Option1","Option2","Option3","Option4","Option5","CorrectAnswer","Explanation"
            </code>
            <p className="text-xs text-muted-foreground mt-1">Note: Empty sub-category shown as empty quotes ("")</p>
          </div>
          
          <div>
            <strong>Pipe-separated:</strong>
            <code className="block mt-1 p-2 bg-muted rounded text-xs">
              Question|Category||Tags|Option1|Option2|Option3|Option4|Option5|CorrectAnswer|Explanation
            </code>
            <p className="text-xs text-muted-foreground mt-1">Note: Empty sub-category shown as double pipe (||)</p>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">💡 Pro Tips:</h4>
            <ul className="text-blue-800 dark:text-blue-200 space-y-1 text-xs">
              <li>• Use Tab + Enter for fast navigation between cells</li>
              <li>• Categories and Tags have auto-completion - just start typing</li>
              <li>• Sub-Category and Tags are optional - leave empty if not needed</li>
              <li>• For empty fields, use consecutive delimiters (,,) or (		) or (||)</li>
              <li>• Option5 can be left empty for 4-option questions</li>
              <li>• Tags should be comma-separated (e.g., "cardiology,ecg")</li>
              <li>• CorrectAnswer must exactly match one of your options</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkPasteTable;